import { useState, useEffect } from "react";
import { PlatformLayout } from "../layouts/PlatformLayout";
import { useIdentity } from "../lib/identity";
import {
  fetchPortalEmployees,
  fetchPortalDepartments,
  fetchCourseAssignments,
  generateDeptAccessCode,
  approveEmployee,
  createCourseAssignment,
  updateAssignmentStatus,
  deleteCourseAssignment,
  updateOrgPortalSettings,
  transferPortalAdmin,
  assignPortalAdmin,
  createDepartment,
  assignDepartmentAdmin,
  removeEmployee,
  createEmployeeInvite,
  revokeInvite,
  fetchPortalInvites,
  assignLicenseToDepartment,
  fetchOrgLicenses,
  fetchOrgOrders,
  fetchOrgInfo,
  updateOrgIndustryPreferences,
  type PortalEmployee,
  type PortalDepartment,
  type CourseAssignment,
  type PortalInvite,
  type PortalOrder,
} from "../lib/portal-api";
import { supabase } from "../lib/supabase";
import { fetchPrograms } from "../lib/sentinel-api";
import type { CertificationProgram } from "../lib/certifications";
import { INDUSTRIES } from "../lib/industries";
import { Users, BookOpen, Building2, Check, X, Copy, RefreshCw, Trash2, Plus, Upload, Shield, UserCog, Send, Mail, ShoppingCart, Tag, UserMinus, Briefcase } from "lucide-react";
import { sendEmail, buildApprovalEmail, buildCourseAssignmentEmail, buildInviteEmail } from "../lib/email-helper";
import { fetchPortalBySlug, type PortalData } from "../lib/portal-api";

type Tab = "employees" | "departments" | "assignments" | "admins" | "orders" | "branding";

export default function PortalAdminPage() {
  const { identity } = useIdentity();
  const [activeTab, setActiveTab] = useState<Tab>("employees");
  const [employees, setEmployees] = useState<PortalEmployee[]>([]);
  const [departments, setDepartments] = useState<PortalDepartment[]>([]);
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [programs, setPrograms] = useState<CertificationProgram[]>([]);
  const [portal, setPortal] = useState<PortalData | null>(null);
  const [invites, setInvites] = useState<PortalInvite[]>([]);
  const [orders, setOrders] = useState<PortalOrder[]>([]);
  const [licenses, setLicenses] = useState<{ id: string; name: string; type: string; department_id: string | null; seats: number }[]>([]);
  const [orgInfo, setOrgInfo] = useState<{ name: string; ein: string | null; portal_slug: string | null; industry: string | null; industry_preferences: string[] | null } | null>(null);
  const [loading, setLoading] = useState(true);

  // Assignment form
  const [assignProgram, setAssignProgram] = useState("");
  const [assignUser, setAssignUser] = useState("");
  const [assignDept, setAssignDept] = useState("");
  const [assignDueDate, setAssignDueDate] = useState("");
  const [assignTarget, setAssignTarget] = useState<"user" | "department">("user");

  // Branding form
  const [logoUrl, setLogoUrl] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [tagline, setTagline] = useState("");
  const [brandingStatus, setBrandingStatus] = useState<"idle" | "saving" | "saved">("idle");

  // Logo upload
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteDept, setInviteDept] = useState("");
  const [inviteRole, setInviteRole] = useState("student");
  const [inviteSending, setInviteSending] = useState(false);

  // Create department form
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptDesc, setNewDeptDesc] = useState("");
  const [deptCreating, setDeptCreating] = useState(false);

  // Industry preferences
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [industrySaving, setIndustrySaving] = useState(false);

  const orgId = identity.organizationId;

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    Promise.all([
      fetchPortalEmployees(orgId),
      fetchPortalDepartments(orgId),
      fetchCourseAssignments(orgId),
      fetchPrograms(),
      fetchPortalInvites(orgId),
      fetchOrgOrders(orgId),
      fetchOrgLicenses(orgId),
      fetchOrgInfo(orgId),
    ]).then(async ([emps, depts, assigns, progs, invs, ords, lics, info]) => {
      setEmployees(emps);
      setDepartments(depts);
      setAssignments(assigns);
      setPrograms(progs);
      setInvites(invs);
      setOrders(ords);
      setLicenses(lics);
      setOrgInfo(info);

      if (info) {
        setPortal({
          id: orgId,
          name: info.name,
          type: "",
          industry: info.industry ?? "",
          logo_url: null,
          portal_primary_color: null,
          portal_tagline: null,
          ein: info.ein,
          portal_slug: info.portal_slug,
          industry_preferences: info.industry_preferences,
          portal_link_visible: true,
        });
        setSelectedIndustries(info.industry_preferences ?? []);
      }

      // Fetch branding data
      const { data: orgData } = await supabase
        .from("sentinel_organizations")
        .select("id, name, portal_slug, logo_url, portal_primary_color, portal_tagline")
        .eq("id", orgId)
        .maybeSingle();
      if (orgData) {
        setPortal((prev) => prev ? { ...prev, logo_url: orgData.logo_url, portal_primary_color: orgData.portal_primary_color, portal_tagline: orgData.portal_tagline } : prev);
        setLogoUrl(orgData.logo_url ?? "");
        setLogoPreview(orgData.logo_url ?? null);
        setPrimaryColor(orgData.portal_primary_color ?? "");
        setTagline(orgData.portal_tagline ?? "");
      }

      setLoading(false);
    });
  }, [orgId]);

  async function handleRefresh() {
    if (!orgId) return;
    const [emps, depts, assigns, invs, ords, lics] = await Promise.all([
      fetchPortalEmployees(orgId),
      fetchPortalDepartments(orgId),
      fetchCourseAssignments(orgId),
      fetchPortalInvites(orgId),
      fetchOrgOrders(orgId),
      fetchOrgLicenses(orgId),
    ]);
    setEmployees(emps);
    setDepartments(depts);
    setAssignments(assigns);
    setInvites(invs);
    setOrders(ords);
    setLicenses(lics);
  }

  async function handleApprove(userId: string, approved: boolean) {
    const result = await approveEmployee(userId, approved);
    if (result) {
      const emp = employees.find((e) => e.id === userId);
      if (emp) {
        const { subject, body } = buildApprovalEmail(emp.name, portal?.name ?? "your company", approved);
        await sendEmail({
          to: emp.email,
          subject,
          body,
          email_type: "employee_approval",
          user_id: userId,
        });
      }
    }
    await handleRefresh();
  }

  async function handleGenerateCode(deptId: string) {
    await generateDeptAccessCode(deptId);
    await handleRefresh();
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  async function handleInvite() {
    if (!orgId || !inviteEmail.trim()) return;
    setInviteSending(true);
    const token = await createEmployeeInvite(
      inviteEmail.trim(),
      inviteDept || undefined,
      inviteRole,
    );
    if (token && orgInfo?.portal_slug) {
      const dept = departments.find((d) => d.id === inviteDept);
      const { subject, body } = buildInviteEmail(
        orgInfo.name,
        orgInfo.portal_slug,
        identity.name ?? "Portal Admin",
        dept?.access_code ?? null,
      );
      await sendEmail({
        to: inviteEmail.trim(),
        subject,
        body,
        email_type: "employee_invite",
      });
      setInviteEmail("");
      setInviteDept("");
      setInviteRole("student");
      await handleRefresh();
    }
    setInviteSending(false);
  }

  async function handleRevokeInvite(inviteId: string) {
    await revokeInvite(inviteId);
    await handleRefresh();
  }

  async function handleRemoveEmployee(userId: string) {
    if (!confirm("Remove this employee from the organization? This cannot be undone if they have started courses.")) return;
    const result = await removeEmployee(userId);
    if (!result.success) {
      alert(result.error ?? "Failed to remove employee");
    } else {
      await handleRefresh();
    }
  }

  async function handleCreateDepartment() {
    if (!orgId || !newDeptName.trim()) return;
    setDeptCreating(true);
    await createDepartment(orgId, newDeptName.trim(), newDeptDesc.trim() || undefined);
    setNewDeptName("");
    setNewDeptDesc("");
    setDeptCreating(false);
    await handleRefresh();
  }

  async function handleAssignDeptAdmin(deptId: string, userId: string) {
    if (!userId) return;
    await assignDepartmentAdmin(deptId, userId);
    await handleRefresh();
  }

  async function handleAssignLicenseToDept(licenseId: string, deptId: string) {
    if (!deptId) return;
    await assignLicenseToDepartment(licenseId, deptId);
    await handleRefresh();
  }

  async function handleSaveIndustries() {
    if (!orgId) return;
    setIndustrySaving(true);
    await updateOrgIndustryPreferences(orgId, selectedIndustries);
    setIndustrySaving(false);
  }

  async function handleCreateAssignment() {
    if (!orgId || !identity.userId || !assignProgram) return;
    const assignment = await createCourseAssignment(
      orgId,
      assignProgram,
      identity.userId,
      assignTarget === "user" ? assignUser || undefined : undefined,
      assignTarget === "department" ? assignDept || undefined : undefined,
      assignDueDate || undefined,
    );
    if (assignment && assignment.user_id) {
      const emp = employees.find((e) => e.id === assignment.user_id);
      const program = programs.find((p) => p.id === assignment.program_id);
      if (emp && program) {
        const { subject, body } = buildCourseAssignmentEmail(emp.name, program.title, assignment.due_date);
        await sendEmail({
          to: emp.email,
          subject,
          body,
          email_type: "course_assignment",
          user_id: emp.id,
          program_id: program.id,
        });
      }
    }
    setAssignProgram("");
    setAssignUser("");
    setAssignDept("");
    setAssignDueDate("");
    await handleRefresh();
  }

  async function handleAssignmentStatus(id: string, status: string) {
    await updateAssignmentStatus(id, status);
    await handleRefresh();
  }

  async function handleDeleteAssignment(id: string) {
    await deleteCourseAssignment(id);
    await handleRefresh();
  }

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !orgId) return;
    setLogoUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "png";
      const path = `company-logos/${orgId}/logo.${ext}`;
      const { error: uploadErr } = await supabase.storage.from("company-assets").upload(path, file, { upsert: true });
      let url: string;
      if (uploadErr) {
        const reader = new FileReader();
        url = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
      } else {
        const { data: urlData } = supabase.storage.from("company-assets").getPublicUrl(path);
        url = urlData.publicUrl;
      }
      setLogoPreview(url);
      setLogoUrl(url);
    } catch { /* ignore */ }
    setLogoUploading(false);
  }

  async function handleSaveBranding() {
    if (!orgId) return;
    setBrandingStatus("saving");
    await updateOrgPortalSettings(orgId, {
      logo_url: logoUrl || undefined,
      portal_primary_color: primaryColor || undefined,
      portal_tagline: tagline || undefined,
    });
    setBrandingStatus("saved");
    setTimeout(() => setBrandingStatus("idle"), 2000);
  }

  if (!orgId) {
    return (
      <PlatformLayout>
        <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
          <Building2 size={48} style={{ color: "var(--text-muted)", marginBottom: "1rem" }} />
          <h2>No Company Portal</h2>
          <p className="text-muted">You are not part of a company organization. Register a company to access portal management.</p>
        </div>
      </PlatformLayout>
    );
  }

  if (loading) {
    return (
      <PlatformLayout>
        <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)" }}>Loading portal management...</div>
      </PlatformLayout>
    );
  }

  const pendingEmployees = employees.filter((e) => e.approval_status === "pending");
  const approvedEmployees = employees.filter((e) => e.approval_status === "approved");
  const portalSlug = orgInfo?.portal_slug ?? "";
  const portalUrl = portalSlug ? `${window.location.origin}/portal/${portalSlug}/register` : "";

  return (
    <PlatformLayout>
      {/* Hero */}
      <div
        style={{
          background: "linear-gradient(135deg, var(--primary-950), var(--primary-800))",
          borderRadius: "var(--radius-2xl)",
          padding: "var(--space-6) var(--space-5)",
          marginBottom: "var(--space-5)",
          color: "var(--text-on-dark)",
        }}
      >
        <h1 style={{ color: "var(--text-on-dark)", marginBottom: "0.5rem" }}>Company Portal Management</h1>
        <p className="lead lead-dark" style={{ fontSize: "1.0625rem", maxWidth: "600px" }}>
          Manage employees, departments, course assignments, orders, and your portal branding.
        </p>
        {orgInfo && (
          <div style={{ marginTop: "var(--space-3)", display: "flex", gap: "var(--space-4)", flexWrap: "wrap", fontSize: "0.8125rem" }}>
            <span style={{ opacity: 0.9 }}><Building2 size={14} style={{ display: "inline", marginRight: "0.25rem" }} />{orgInfo.name}</span>
            {orgInfo.ein && <span style={{ opacity: 0.9 }}>EIN: {orgInfo.ein}</span>}
            {portalUrl && (
              <span style={{ opacity: 0.9 }}>
                Portal: <code style={{ color: "var(--accent-300)" }}>{portalUrl}</code>
                <button onClick={() => copyToClipboard(portalUrl)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--accent-300)", marginLeft: "0.25rem", padding: 0, verticalAlign: "middle" }} title="Copy link">
                  <Copy size={12} style={{ display: "inline" }} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Pending approvals banner */}
      {pendingEmployees.length > 0 && (
        <div className="card" style={{ padding: "var(--space-3) var(--space-4)", borderColor: "var(--warning-400)", background: "var(--warning-50)", marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <Users size={20} style={{ color: "var(--warning-600)" }} />
          <span style={{ fontSize: "0.875rem", fontWeight: 600, color: "var(--warning-700)" }}>
            {pendingEmployees.length} employee{pendingEmployees.length > 1 ? "s" : ""} pending approval
          </span>
          <button onClick={() => setActiveTab("employees")} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.25rem 0.625rem", marginLeft: "auto" }}>
            Review
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: "0.25rem", marginBottom: "var(--space-4)", borderBottom: "2px solid var(--border)", flexWrap: "wrap" }}>
        {([
          { key: "employees", label: `Employees (${employees.length})` },
          { key: "departments", label: `Departments (${departments.length})` },
          { key: "assignments", label: `Assignments (${assignments.length})` },
          { key: "admins", label: "Portal Admins" },
          { key: "orders", label: `Orders (${orders.length})` },
          { key: "branding", label: "Settings" },
        ] as const).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "0.625rem 1rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              background: "transparent",
              border: "none",
              borderBottom: activeTab === tab.key ? "2px solid var(--accent-500)" : "2px solid transparent",
              color: activeTab === tab.key ? "var(--accent-600)" : "var(--text-muted)",
              cursor: "pointer",
              marginBottom: "-2px",
              transition: "all var(--transition-fast)",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Employees Tab */}
      {activeTab === "employees" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {/* Invite employee */}
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Mail size={18} /> Invite Employee
            </h3>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
              Send an email invitation with a link to your portal registration page. The employee will receive instructions to create their account.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "0.75rem", alignItems: "end" }}>
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Email Address</label>
                <input type="email" className="form-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="employee@company.com" style={{ fontSize: "0.8125rem" }} />
              </div>
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Department</label>
                <select className="form-input" value={inviteDept} onChange={(e) => setInviteDept(e.target.value)} style={{ fontSize: "0.8125rem" }}>
                  <option value="">No specific department</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Role</label>
                <select className="form-input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} style={{ fontSize: "0.8125rem" }}>
                  <option value="student">Employee</option>
                  <option value="manager">Department Manager</option>
                </select>
              </div>
              <button onClick={handleInvite} className="btn btn-primary" disabled={inviteSending} style={{ fontSize: "0.8125rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                <Send size={14} /> {inviteSending ? "Sending..." : "Send Invite"}
              </button>
            </div>
          </div>

          {/* Pending invites */}
          {invites.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ padding: "var(--space-3) var(--space-4)", borderBottom: "2px solid var(--border)", fontWeight: 700, fontSize: "0.875rem" }}>
                Pending Invitations
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    {["Email", "Department", "Role", "Status", "Invited By", "Expires", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.5rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => (
                    <tr key={inv.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{inv.email}</td>
                      <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{inv.department_name ?? "—"}</td>
                      <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", textTransform: "capitalize" }}>{inv.role.replace(/_/g, " ")}</td>
                      <td style={{ padding: "0.5rem 1rem" }}>
                        <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", fontWeight: 600, background: inv.status === "pending" ? "var(--warning-50)" : "var(--accent-50)", color: inv.status === "pending" ? "var(--warning-600)" : "var(--accent-600)" }}>{inv.status}</span>
                      </td>
                      <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{inv.inviter_name ?? "—"}</td>
                      <td style={{ padding: "0.5rem 1rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                      <td style={{ padding: "0.5rem 1rem" }}>
                        {inv.status === "pending" && (
                          <button onClick={() => handleRevokeInvite(inv.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--error-500)", padding: "0.25rem" }} title="Revoke invite">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending approvals */}
          {pendingEmployees.length > 0 && (
            <>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--warning-700)" }}>Pending Approval</h3>
              {pendingEmployees.map((emp) => (
                <div key={emp.id} className="card" style={{ padding: "var(--space-3) var(--space-4)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{emp.name}</div>
                    <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>{emp.email}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      Department: {emp.department_name ?? "Unassigned"} &middot; Registered: {new Date(emp.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button onClick={() => handleApprove(emp.id, true)} className="btn btn-primary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                      <Check size={14} /> Approve
                    </button>
                    <button onClick={() => handleApprove(emp.id, false)} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", display: "flex", alignItems: "center", gap: "0.25rem", color: "var(--error-600)" }}>
                      <X size={14} /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}

          <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, marginTop: pendingEmployees.length > 0 ? "1rem" : 0 }}>All Employees</h3>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Name", "Email", "Role", "Department", "Status", "Certs", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {employees.map((emp) => (
                    <tr key={emp.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{emp.name}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{emp.email}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", textTransform: "capitalize" }}>{emp.role.replace(/_/g, " ")}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{emp.department_name ?? "—"}</td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        <span style={{
                          fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", fontWeight: 600,
                          background: emp.approval_status === "approved" ? "var(--accent-50)" : emp.approval_status === "pending" ? "var(--warning-50)" : "var(--error-50)",
                          color: emp.approval_status === "approved" ? "var(--accent-600)" : emp.approval_status === "pending" ? "var(--warning-600)" : "var(--error-600)",
                        }}>{emp.approval_status}</span>
                      </td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{emp.completed_certifications?.length ?? 0}</td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        {emp.role !== "enterprise_admin" && emp.role !== "agency_admin" && emp.role !== "platform_admin" && emp.approval_status === "approved" && (
                          <button onClick={() => handleRemoveEmployee(emp.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--error-500)", padding: "0.25rem" }} title="Remove employee">
                            <UserMinus size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {employees.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No employees yet. Invite employees using the form above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Departments Tab */}
      {activeTab === "departments" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          {/* Create department */}
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Plus size={18} /> Create Department
            </h3>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Department Name</label>
                <input className="form-input" value={newDeptName} onChange={(e) => setNewDeptName(e.target.value)} placeholder="e.g. Operations" style={{ fontSize: "0.8125rem" }} />
              </div>
              <div style={{ flex: 1, minWidth: "200px" }}>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Description (optional)</label>
                <input className="form-input" value={newDeptDesc} onChange={(e) => setNewDeptDesc(e.target.value)} placeholder="e.g. Field operations team" style={{ fontSize: "0.8125rem" }} />
              </div>
              <button onClick={handleCreateDepartment} className="btn btn-primary" disabled={deptCreating} style={{ fontSize: "0.8125rem" }}>
                {deptCreating ? "Creating..." : "Create"}
              </button>
            </div>
          </div>

          {departments.map((dept) => (
            <div key={dept.id} className="card" style={{ padding: "var(--space-4)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
                <div style={{ flex: 1, minWidth: "250px" }}>
                  <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.25rem" }}>{dept.name}</h3>
                  {dept.description && <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "0.25rem" }}>{dept.description}</p>}
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                    {dept.employee_count} employee{dept.employee_count !== 1 ? "s" : ""}
                    {dept.budget != null && ` · Budget: $${dept.budget.toLocaleString()}`}
                    {dept.admin_name && ` · Admin: ${dept.admin_name}`}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.5rem" }}>
                  {dept.access_code ? (
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <code style={{ fontFamily: "monospace", fontSize: "1rem", fontWeight: 700, letterSpacing: "0.15em", padding: "0.375rem 0.75rem", borderRadius: "var(--radius-md)", background: "var(--accent-50)", color: "var(--accent-700)", border: "1px solid var(--accent-200)" }}>
                        {dept.access_code}
                      </code>
                      <button onClick={() => copyToClipboard(dept.access_code!)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: "0.25rem" }} title="Copy code">
                        <Copy size={16} />
                      </button>
                    </div>
                  ) : (
                    <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>No access code generated</span>
                  )}
                  <button onClick={() => handleGenerateCode(dept.id)} className="btn btn-secondary" style={{ fontSize: "0.75rem", padding: "0.375rem 0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                    <RefreshCw size={12} /> {dept.access_code ? "Regenerate" : "Generate Code"}
                  </button>
                </div>
              </div>

              {dept.access_code && portalUrl && (
                <div style={{ marginTop: "0.75rem", padding: "0.75rem", borderRadius: "var(--radius-md)", background: "var(--bg-subtle)", fontSize: "0.8125rem" }}>
                  <strong>Share this link with employees:</strong>
                  <br />
                  <code style={{ fontSize: "0.75rem", color: "var(--accent-600)" }}>{portalUrl}</code>
                  <br />
                  <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Employees use the access code above to register into this department.</span>
                </div>
              )}

              {/* Assign department admin */}
              <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: "200px" }}>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Department Administrator</label>
                  <select
                    className="form-input"
                    defaultValue={dept.admin_user_id ?? ""}
                    onChange={(e) => handleAssignDeptAdmin(dept.id, e.target.value)}
                    style={{ fontSize: "0.8125rem" }}
                  >
                    <option value="">Select an employee...</option>
                    {approvedEmployees.map((emp) => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Assign licenses to department */}
              {licenses.length > 0 && (
                <div style={{ marginTop: "0.75rem", display: "flex", gap: "0.75rem", alignItems: "end", flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: "200px" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Assign License to Department</label>
                    <select
                      className="form-input"
                      defaultValue=""
                      onChange={(e) => { if (e.target.value) handleAssignLicenseToDept(e.target.value, dept.id); }}
                      style={{ fontSize: "0.8125rem" }}
                    >
                      <option value="">Select a license...</option>
                      {licenses.map((lic) => (
                        <option key={lic.id} value={lic.id}>{lic.name} ({lic.seats} seats){lic.department_id ? " — already assigned" : ""}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
          {departments.length === 0 && (
            <div className="card" style={{ padding: "var(--space-6)", textAlign: "center" }}>
              <p className="text-muted">No departments yet. Create a department using the form above.</p>
            </div>
          )}
        </div>
      )}

      {/* Assignments Tab */}
      {activeTab === "assignments" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Plus size={18} /> Assign a Course
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr 1fr 1fr auto", gap: "0.75rem", alignItems: "end" }}>
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Assign To</label>
                <select className="form-input" value={assignTarget} onChange={(e) => setAssignTarget(e.target.value as "user" | "department")} style={{ fontSize: "0.8125rem" }}>
                  <option value="user">Individual</option>
                  <option value="department">Department</option>
                </select>
              </div>
              {assignTarget === "user" ? (
                <div>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Employee</label>
                  <select className="form-input" value={assignUser} onChange={(e) => setAssignUser(e.target.value)} style={{ fontSize: "0.8125rem" }}>
                    <option value="">Select employee...</option>
                    {approvedEmployees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Department</label>
                  <select className="form-input" value={assignDept} onChange={(e) => setAssignDept(e.target.value)} style={{ fontSize: "0.8125rem" }}>
                    <option value="">Select department...</option>
                    {departments.map((dept) => <option key={dept.id} value={dept.id}>{dept.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Course</label>
                <select className="form-input" value={assignProgram} onChange={(e) => setAssignProgram(e.target.value)} style={{ fontSize: "0.8125rem" }}>
                  <option value="">Select course...</option>
                  {programs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                </select>
              </div>
              <div>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Due Date (optional)</label>
                <input type="date" className="form-input" value={assignDueDate} onChange={(e) => setAssignDueDate(e.target.value)} style={{ fontSize: "0.8125rem" }} />
              </div>
              <button onClick={handleCreateAssignment} className="btn btn-primary" style={{ fontSize: "0.8125rem" }}>Assign</button>
            </div>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Course", "Assigned To", "Type", "Status", "Due Date", "Assigned", ""].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assignments.map((a) => {
                    const program = programs.find((p) => p.id === a.program_id);
                    return (
                      <tr key={a.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{program?.title ?? a.program_id}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{a.user_name ?? a.department_name ?? "—"}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{a.user_id ? "Individual" : "Department"}</td>
                        <td style={{ padding: "0.625rem 1rem" }}>
                          <select value={a.status} onChange={(e) => handleAssignmentStatus(a.id, e.target.value)} style={{ fontSize: "0.6875rem", padding: "0.125rem 0.375rem", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", background: a.status === "completed" ? "var(--accent-50)" : a.status === "revoked" ? "var(--error-50)" : "transparent", fontWeight: 600, cursor: "pointer" }}>
                            <option value="assigned">assigned</option>
                            <option value="in_progress">in_progress</option>
                            <option value="completed">completed</option>
                            <option value="revoked">revoked</option>
                          </select>
                        </td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{a.due_date ?? "—"}</td>
                        <td style={{ padding: "0.625rem 1rem", fontSize: "0.75rem", color: "var(--text-muted)" }}>{new Date(a.created_at).toLocaleDateString()}</td>
                        <td style={{ padding: "0.625rem 1rem" }}>
                          <button onClick={() => handleDeleteAssignment(a.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--error-500)", padding: "0.25rem" }} title="Delete assignment">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {assignments.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No course assignments yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Portal Admins Tab */}
      {activeTab === "admins" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div className="card" style={{ padding: "var(--space-4)", borderColor: "var(--accent-200)", background: "var(--accent-50)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
              <Shield size={18} style={{ color: "var(--accent-600)" }} />
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "var(--accent-700)" }}>Portal Administrator Management</h3>
            </div>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", margin: 0 }}>
              Promote other employees to portal admin so they can manage the company portal alongside you,
              or transfer your admin rights to someone else (you'll be demoted to manager).
              The new admin will receive an email with instructions.
            </p>
          </div>

          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "var(--space-3) var(--space-4)", borderBottom: "2px solid var(--border)", fontWeight: 700, fontSize: "0.875rem" }}>Current Portal Admins</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["Name", "Email", "Role", ""].map((h) => (
                    <th key={h} style={{ textAlign: "left", padding: "0.5rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.filter((e) => e.role === "enterprise_admin" || e.role === "agency_admin" || e.role === "platform_admin").map((emp) => (
                  <tr key={emp.id} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{emp.name}</td>
                    <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)" }}>{emp.email}</td>
                    <td style={{ padding: "0.5rem 1rem", fontSize: "0.8125rem", textTransform: "capitalize" }}>{emp.role.replace(/_/g, " ")}</td>
                    <td style={{ padding: "0.5rem 1rem" }}>
                      {emp.id === identity.userId && <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", background: "var(--accent-100)", color: "var(--accent-700)", fontWeight: 600 }}>You</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "var(--space-3)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <UserCog size={18} /> Promote or Transfer Admin Rights
            </h3>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginBottom: "var(--space-3)" }}>
              Select an employee to promote them to portal admin. You can either promote them alongside you,
              or transfer your admin rights to them (you'll become a manager).
            </p>
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "end", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: "250px" }}>
                <label className="form-label" style={{ fontSize: "0.75rem" }}>Select Employee</label>
                <select id="admin-target-select" className="form-input" style={{ fontSize: "0.8125rem" }}>
                  <option value="">Select an employee to promote...</option>
                  {employees.filter((e) => e.role !== "enterprise_admin" && e.role !== "agency_admin" && e.role !== "platform_admin" && e.approval_status === "approved").map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.name} ({emp.email})</option>
                  ))}
                </select>
              </div>
              <button
                onClick={async () => {
                  const select = document.getElementById("admin-target-select") as HTMLSelectElement;
                  const targetId = select.value;
                  if (!targetId) return;
                  const success = await assignPortalAdmin(targetId);
                  if (success) {
                    const emp = employees.find((e) => e.id === targetId);
                    if (emp && portal) {
                      await sendEmail({
                        to: emp.email,
                        subject: `You've been promoted to Portal Admin at ${portal.name}`,
                        body: `Hi ${emp.name},\n\nYou've been promoted to portal administrator for ${portal.name}.\n\nAs portal admin, you can:\n- Approve or reject employee registrations\n- Generate department access codes\n- Assign courses to employees and departments\n- Manage portal branding (logo, colors, tagline)\n- Promote or transfer admin rights\n\nVisit ${window.location.origin}/portal/manage to get started.\n\n— The Sentinel Team`,
                        email_type: "admin_assignment",
                        user_id: emp.id,
                      });
                    }
                    await handleRefresh();
                  }
                }}
                className="btn btn-secondary"
                style={{ fontSize: "0.8125rem" }}
              >
                <Plus size={14} /> Promote to Admin
              </button>
              <button
                onClick={async () => {
                  const select = document.getElementById("admin-target-select") as HTMLSelectElement;
                  const targetId = select.value;
                  if (!targetId) return;
                  if (!confirm("This will transfer your admin rights to this person and demote you to manager. Continue?")) return;
                  const success = await transferPortalAdmin(targetId, true);
                  if (success) {
                    const emp = employees.find((e) => e.id === targetId);
                    if (emp && portal) {
                      await sendEmail({
                        to: emp.email,
                        subject: `You are now the Portal Admin for ${portal.name}`,
                        body: `Hi ${emp.name},\n\nYou have been assigned as the portal administrator for ${portal.name}.\n\nAs portal admin, you can:\n- Approve or reject employee registrations\n- Generate department access codes\n- Assign courses to employees and departments\n- Manage portal branding (logo, colors, tagline)\n- Promote or transfer admin rights\n\nVisit ${window.location.origin}/portal/manage to get started.\n\n— The Sentinel Team`,
                        email_type: "admin_transfer",
                        user_id: emp.id,
                      });
                    }
                    await handleRefresh();
                  }
                }}
                className="btn btn-primary"
                style={{ fontSize: "0.8125rem", display: "flex", alignItems: "center", gap: "0.25rem" }}
              >
                <Send size={14} /> Transfer My Admin Rights
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <div className="card" style={{ padding: "var(--space-4)" }}>
            <h3 style={{ fontSize: "1rem", fontWeight: 700, marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <ShoppingCart size={18} /> Order History
            </h3>
            <p style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>All purchases made for this organization, including licenses and individual courses.</p>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    {["Date", "Item", "Type", "Quantity", "Amount", "Status", "Receipt"].map((h) => (
                      <th key={h} style={{ textAlign: "left", padding: "0.625rem 1rem", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--neutral-500)" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{new Date(o.created_at).toLocaleDateString()}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>{o.item_name}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", color: "var(--text-muted)", textTransform: "capitalize" }}>{o.order_type.replace(/_/g, " ")}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem" }}>{o.quantity}</td>
                      <td style={{ padding: "0.625rem 1rem", fontSize: "0.8125rem", fontWeight: 600 }}>${(o.amount / 100).toFixed(2)}</td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        <span style={{ fontSize: "0.6875rem", padding: "0.125rem 0.5rem", borderRadius: "var(--radius-full)", fontWeight: 600, background: o.status === "paid" ? "var(--accent-50)" : "var(--warning-50)", color: o.status === "paid" ? "var(--accent-600)" : "var(--warning-600)" }}>{o.status}</span>
                      </td>
                      <td style={{ padding: "0.625rem 1rem" }}>
                        {o.receipt_url ? (
                          <a href={o.receipt_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.75rem", color: "var(--accent-600)" }}>Download</a>
                        ) : o.invoice_url ? (
                          <a href={o.invoice_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: "0.75rem", color: "var(--accent-600)" }}>Invoice</a>
                        ) : (
                          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>No orders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab (Branding + Industry) */}
      {activeTab === "branding" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
          {/* Industry preferences */}
          <div className="card" style={{ padding: "var(--space-5)" }}>
            <h3 style={{ fontSize: "1.125rem", marginBottom: "var(--space-2)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Tag size={18} /> Industry Preferences
            </h3>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-3)" }}>
              Select the industries your company works in. This filters the certification catalog so your employees only see relevant courses. You can add more industries at any time.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "var(--space-3)" }}>
              {INDUSTRIES.map((ind) => {
                const selected = selectedIndustries.includes(ind.slug);
                return (
                  <button
                    key={ind.slug}
                    onClick={() => {
                      setSelectedIndustries((prev) =>
                        selected ? prev.filter((s) => s !== ind.slug) : [...prev, ind.slug],
                      );
                    }}
                    style={{
                      padding: "0.375rem 0.75rem",
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      borderRadius: "var(--radius-full)",
                      border: selected ? "1px solid var(--accent-500)" : "1px solid var(--border)",
                      background: selected ? "var(--accent-50)" : "transparent",
                      color: selected ? "var(--accent-700)" : "var(--text-muted)",
                      cursor: "pointer",
                      transition: "all var(--transition-fast)",
                    }}
                  >
                    {ind.name}
                  </button>
                );
              })}
            </div>
            <button onClick={handleSaveIndustries} className="btn btn-primary" disabled={industrySaving} style={{ fontSize: "0.8125rem" }}>
              {industrySaving ? "Saving..." : "Save Industry Preferences"}
            </button>
          </div>

          {/* Portal branding */}
          <div className="card" style={{ padding: "var(--space-5)", maxWidth: "600px" }}>
            <h3 style={{ fontSize: "1.125rem", marginBottom: "var(--space-4)", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <Building2 size={18} /> Portal Branding
            </h3>
            <p className="text-muted" style={{ fontSize: "0.875rem", marginBottom: "var(--space-4)" }}>
              Customize your company portal page with your logo, brand color, and tagline.
            </p>
            <div className="form-group">
              <label className="form-label">Company Logo</label>
              <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" style={{ maxWidth: "100px", maxHeight: "100px", borderRadius: "var(--radius-md)", border: "1px solid var(--border)" }} />
                ) : (
                  <div style={{ width: "100px", height: "100px", borderRadius: "var(--radius-md)", border: "2px dashed var(--border)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-muted)" }}>
                    <Building2 size={24} />
                  </div>
                )}
                <label className="btn btn-secondary" style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.375rem" }}>
                  <Upload size={14} /> {logoUploading ? "Uploading..." : "Upload Logo"}
                  <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogoUpload} style={{ display: "none" }} />
                </label>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="portal-color">Primary Brand Color</label>
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                <input type="color" value={primaryColor || "#1e3a5f"} onChange={(e) => setPrimaryColor(e.target.value)} style={{ width: "40px", height: "40px", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)", cursor: "pointer" }} />
                <input id="portal-color" className="form-input" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} placeholder="#1e3a5f" style={{ flex: 1, fontFamily: "monospace" }} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="portal-tagline">Portal Tagline</label>
              <input id="portal-tagline" className="form-input" value={tagline} onChange={(e) => setTagline(e.target.value)} placeholder="e.g. Building safer teams together" />
            </div>
            <button onClick={handleSaveBranding} className="btn btn-primary" disabled={brandingStatus === "saving"} style={{ marginTop: "var(--space-3)" }}>
              {brandingStatus === "saving" ? "Saving..." : brandingStatus === "saved" ? "Saved!" : "Save Branding"}
            </button>
          </div>
        </div>
      )}
    </PlatformLayout>
  );
}
